<!--

 -->

<template>
  <div>
    <div class="gsl-hrm-horizontal-padding gsl-hrm-vertical-padding">
      <oxd-text tag="h6" class="gsl-hrm-main-title">
        {{ $t('pim.add_immigration') }}
      </oxd-text>
      <oxd-divider />
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-group
                :label="$t('pim.document')"
                :classes="{wrapper: '--gender-grouped-field'}"
              >
                <oxd-input-field
                  v-model="immigration.type"
                  type="radio"
                  :option-label="$t('pim.passport')"
                  value="1"
                />
                <oxd-input-field
                  v-model="immigration.type"
                  type="radio"
                  :option-label="$t('pim.visa')"
                  value="2"
                />
                <oxd-input-field
                  v-model="immigration.type"
                  type="radio"
                  :option-label="$t('pim.other')"
                  value="3"
                />
              </oxd-input-group>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="immigration.number"
                :label="$t('pim.number')"
                :rules="rules.number"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="immigration.issuedDate"
                :label="$t('pim.issued_date')"
                :rules="rules.issuedDate"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="immigration.expiryDate"
                :label="$t('general.expiry_date')"
                :years="yearArray"
                :rules="rules.expiryDate"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="immigration.status"
                :label="$t('pim.eligible_status')"
                :rules="rules.status"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="immigration.countryCode"
                type="select"
                :label="$t('pim.issued_by')"
                :options="countries"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <div class="date-checkbox-container">
                <oxd-label class="custom-label">{{
                  $t('pim.eligible_review_date')
                }}</oxd-label>

                <oxd-check-box
                  v-model="immigration.notify"
                  value="true"
                  :true-value="true"
                  :false-value="false"
                  option-label="Notify"
                  class="custom-check-box"
                  style="margin-bottom: 2px"
                />
              </div>
              <date-input
                v-model="immigration.reviewDate"
                :rules="rules.reviewDate"
                class="full-width-input"
              />
            </oxd-grid-item>

            <oxd-grid-item>
              <oxd-input-field
                v-model="immigration.comment"
                type="textarea"
                :label="$t('general.comments')"
                :placeholder="$t('general.type_comments_here')"
                :rules="rules.comment"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-actions>
          <required-text />
          <oxd-button
            type="button"
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
    <oxd-divider />
  </div>
</template>

<script>
import {
  required,
  shouldNotExceedCharLength,
  validDateFormat,
  endDateShouldBeAfterStartDate,
  startDateShouldBeBeforeEndDate,
} from '@ohrm/core/util/validation/rules';
import {yearRange} from '@ohrm/core/util/helper/year-range';
import useDateFormat from '@/core/util/composable/useDateFormat';

const immigrationModel = {
  number: '',
  issuedDate: '',
  expiryDate: '',
  type: 1,
  status: '',
  reviewDate: '',
  countryCode: null,
  comment: '',
  notify: false,
};

export default {
  name: 'SaveImmigration',

  props: {
    http: {
      type: Object,
      required: true,
    },
    countries: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['close'],

  setup() {
    const {userDateFormat} = useDateFormat();

    return {
      userDateFormat,
    };
  },

  data() {
    return {
      dateRange: [
        {id: 1, label: this.$t('pim.one_month_before')},
        {id: 2, label: this.$t('pim.two_month_before')},
        {id: 3, label: this.$t('pim.three_month_before')},
        {id: 4, label: this.$t('pim.four_month_before')},
        {id: 5, label: this.$t('pim.five_month_before')},
        {id: 6, label: this.$t('pim.six_month_before')},
      ],
      isLoading: false,
      immigration: {...immigrationModel},
      yearArray: [...yearRange()],
      rules: {
        number: [required, shouldNotExceedCharLength(30)],
        expiryDate: [
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.immigration.issuedDate,
            this.$t('pim.expiry_date_should_be_after_issued_date'),
          ),
        ],
        status: [shouldNotExceedCharLength(30)],
        issuedDate: [validDateFormat(this.userDateFormat)],
        reviewDate: [
          validDateFormat(this.userDateFormat),
          startDateShouldBeBeforeEndDate(
            () => this.immigration.expiryDate,
            this.$t('pim.review_date_should_be_before_expiry_date'),
          ),
        ],
        comment: [shouldNotExceedCharLength(250)],
      },
    };
  },

  methods: {
    onSave() {
      this.isLoading = true;
      this.http
        .create({
          ...this.immigration,
          countryCode: this.immigration.countryCode?.id,
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.immigration = {...immigrationModel};
          this.onCancel();
        });
    },
    onCancel() {
      this.$emit('close', true);
    },
  },
};
</script>
<style scoped lang="scss">
.date-checkbox-container {
  display: flex;
  align-items: center;
  justify-content: space-between; /* Ensures label is on the left and checkbox is on the right */
  width: 100%;
}

.custom-label {
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--oxd-interface-gray-darken-1-color);
}
.custom-check-box {
  margin-left: auto;
  margin-bottom: 2px;
}

.full-width-input {
  width: 100%;
}
</style>
